export const platformModifiers = {
  facebook: { cplMult: 0.8, closeMult: 0.85, name: 'Facebook' },
  instagram: { cplMult: 0.9, closeMult: 0.85, name: 'Instagram' },
  googleAds: { cplMult: 0.9, closeMult: 1.0, name: 'Google Ads' },
  googleSearch: { cplMult: 1.3, closeMult: 1.2, name: 'Google Search' },
  googleMaps: { cplMult: 0.4, closeMult: 1.4, name: 'Google Maps' },
  tiktok: { cplMult: 0.7, closeMult: 0.7, name: 'TikTok' },
}

export type Platform = keyof typeof platformModifiers

export interface IndustryToggleEffect {
  ltvMultiplier?: number
  ticketMultiplier?: number
  marginBoost?: number
  conversionMultiplier?: number
}

export interface IndustryData {
  name: string
  cpl: number
  leadToAppt: number
  apptToShow: number
  showToPurchase: number
  firstVisitTicket: number
  goBack12mo: number
  ltv5yr: number
  margin: number
  referralMultiplier: number
  distribution: Record<Platform, number>
  toggle: {
    label: string
    default: number
    stretch: number
    isToggle?: boolean
    description: string
    effect: (value: number) => IndustryToggleEffect
  }
}

export const industries: Record<string, IndustryData> = {
  medspa: {
    name: 'Med Spa',
    cpl: 100,
    leadToAppt: 0.38,
    apptToShow: 0.6,
    showToPurchase: 0.8,
    firstVisitTicket: 504,
    goBack12mo: 2400,
    ltv5yr: 10000,
    margin: 0.22,
    referralMultiplier: 1.35,
    distribution: {
      facebook: 25,
      instagram: 25,
      googleAds: 15,
      googleSearch: 20,
      googleMaps: 10,
      tiktok: 5,
    },
    toggle: {
      label: 'Conversion %',
      default: 25,
      stretch: 40,
      description: 'Tracks lead-to-show conversion rate',
      effect: (value) => ({
        ltvMultiplier: 1 + (value / 100) * 1.5,
        marginBoost: (value / 100) * 0.05,
        conversionMultiplier: 0.5 + (value / 100) * 1.5,
      }),
    },
  },
  hvac: {
    name: 'HVAC',
    cpl: 85,
    leadToAppt: 0.45,
    apptToShow: 0.8,
    showToPurchase: 0.42,
    firstVisitTicket: 1200,
    goBack12mo: 2100,
    ltv5yr: 15340,
    margin: 0.14,
    referralMultiplier: 1.2,
    distribution: {
      facebook: 10,
      instagram: 5,
      googleAds: 25,
      googleSearch: 45,
      googleMaps: 15,
      tiktok: 0,
    },
    toggle: {
      label: 'Maintenance Plan Attach %',
      default: 25,
      stretch: 50,
      description: '% of new customers signed to annual maintenance plans',
      effect: (value) => ({
        ltvMultiplier: 1 + (value / 100) * 1.4,
        marginBoost: (value / 100) * 0.04,
      }),
    },
  },
  plumbing: {
    name: 'Plumbing',
    cpl: 65,
    leadToAppt: 0.55,
    apptToShow: 0.85,
    showToPurchase: 0.48,
    firstVisitTicket: 450,
    goBack12mo: 1200,
    ltv5yr: 4200,
    margin: 0.15,
    referralMultiplier: 1.25,
    distribution: {
      facebook: 10,
      instagram: 5,
      googleAds: 20,
      googleSearch: 50,
      googleMaps: 15,
      tiktok: 0,
    },
    toggle: {
      label: 'Membership Plan Attach %',
      default: 15,
      stretch: 35,
      description: '% of new customers signed to plumbing service plans',
      effect: (value) => ({
        ltvMultiplier: 1 + (value / 100) * 1.3,
        marginBoost: (value / 100) * 0.03,
      }),
    },
  },
  roofing: {
    name: 'Roofing',
    cpl: 125,
    leadToAppt: 0.4,
    apptToShow: 0.7,
    showToPurchase: 0.28,
    firstVisitTicket: 9500,
    goBack12mo: 9500,
    ltv5yr: 12500,
    margin: 0.14,
    referralMultiplier: 1.4,
    distribution: {
      facebook: 25,
      instagram: 10,
      googleAds: 20,
      googleSearch: 30,
      googleMaps: 10,
      tiktok: 5,
    },
    toggle: {
      label: 'Storm Market',
      default: 0,
      stretch: 1,
      isToggle: true,
      description: 'Storm market + insurance supplementing active',
      effect: (value) => ({
        ticketMultiplier: value ? 1.21 : 1,
        marginBoost: value ? 0.04 : 0,
      }),
    },
  },
  pestcontrol: {
    name: 'Pest Control',
    cpl: 55,
    leadToAppt: 0.5,
    apptToShow: 0.78,
    showToPurchase: 0.38,
    firstVisitTicket: 200,
    goBack12mo: 600,
    ltv5yr: 3000,
    margin: 0.22,
    referralMultiplier: 1.15,
    distribution: {
      facebook: 15,
      instagram: 10,
      googleAds: 20,
      googleSearch: 40,
      googleMaps: 15,
      tiktok: 0,
    },
    toggle: {
      label: 'Subscription Conversion %',
      default: 40,
      stretch: 70,
      description: '% of new customers converted to recurring plans',
      effect: (value) => ({
        ltvMultiplier: 1 + (value / 100) * 2.0,
        marginBoost: (value / 100) * 0.06,
      }),
    },
  },
  painting: {
    name: 'Painting',
    cpl: 95,
    leadToAppt: 0.42,
    apptToShow: 0.72,
    showToPurchase: 0.32,
    firstVisitTicket: 3500,
    goBack12mo: 3800,
    ltv5yr: 6500,
    margin: 0.18,
    referralMultiplier: 1.5,
    distribution: {
      facebook: 25,
      instagram: 15,
      googleAds: 20,
      googleSearch: 25,
      googleMaps: 10,
      tiktok: 5,
    },
    toggle: {
      label: 'Service Mix (Commercial %)',
      default: 20,
      stretch: 50,
      description: '% of jobs that are commercial (higher ticket + recurring)',
      effect: (value) => ({
        ticketMultiplier: 1 + (value / 100) * 0.4,
        marginBoost: (value / 100) * 0.05,
      }),
    },
  },
  junkremoval: {
    name: 'Junk Removal',
    cpl: 45,
    leadToAppt: 0.6,
    apptToShow: 0.82,
    showToPurchase: 0.6,
    firstVisitTicket: 450,
    goBack12mo: 750,
    ltv5yr: 1350,
    margin: 0.28,
    referralMultiplier: 1.2,
    distribution: {
      facebook: 15,
      instagram: 10,
      googleAds: 25,
      googleSearch: 35,
      googleMaps: 15,
      tiktok: 0,
    },
    toggle: {
      label: 'Commercial Contract %',
      default: 20,
      stretch: 40,
      description: '% of revenue from property management/commercial contracts',
      effect: (value) => ({
        ltvMultiplier: 1 + (value / 100) * 1.5,
        marginBoost: (value / 100) * 0.05,
      }),
    },
  },
  pressurewashing: {
    name: 'Pressure Washing',
    cpl: 55,
    leadToAppt: 0.52,
    apptToShow: 0.8,
    showToPurchase: 0.48,
    firstVisitTicket: 650,
    goBack12mo: 900,
    ltv5yr: 2800,
    margin: 0.3,
    referralMultiplier: 1.3,
    distribution: {
      facebook: 30,
      instagram: 15,
      googleAds: 15,
      googleSearch: 25,
      googleMaps: 10,
      tiktok: 5,
    },
    toggle: {
      label: 'Package Pricing',
      default: 0,
      stretch: 1,
      isToggle: true,
      description: 'Bundled services (house + driveway + roof soft-wash)',
      effect: (value) => ({
        ticketMultiplier: value ? 2.15 : 1,
        marginBoost: value ? 0.02 : 0,
      }),
    },
  },
  ecom: {
    name: 'E-Commerce',
    cpl: 3.0,
    leadToAppt: 0.1,
    apptToShow: 0.5,
    showToPurchase: 0.6,
    firstVisitTicket: 95,
    goBack12mo: 220,
    ltv5yr: 720,
    margin: 0.4,
    referralMultiplier: 1.3,
    distribution: {
      facebook: 30,
      instagram: 25,
      googleAds: 15,
      googleSearch: 10,
      googleMaps: 0,
      tiktok: 20,
    },
    toggle: {
      label: 'Email/SMS Capture %',
      default: 30,
      stretch: 60,
      description: '% of visitors captured into email/SMS for repeat purchases',
      effect: (value) => ({
        ltvMultiplier: 1 + (value / 100) * 1.8,
        marginBoost: (value / 100) * 0.04,
        conversionMultiplier: 0.7 + (value / 100) * 1.0,
      }),
    },
  },
}
